// ANGEL FLIGHT — BriefingScreen (DOM). The pre-deploy mission card.
//
// The authored MissionDef.brief strings (the radio voice that carries the
// CHECK-YOUR-FIRE rule the IFF mechanic depends on) finally render: mission
// name/biome/objective, the brief as an OVERLORD transmission with a typewriter
// reveal (tap the card to skip), an ROE chip when BLUEFOR walks the grid, a
// difficulty selector (it takes effect on THIS deploy), and the DEPLOY confirm.
// Shown between the menu's mission pick and startMission; BACK returns to the
// menu. ?dev= quickstarts skip it by design (straight into the mission).

import type { Screen } from '../Router';
import type { SaveStateV1, Difficulty } from '../../core/types';
import { writeSave } from '../../core/storage';
import { getMission } from '../../missions/index';
import { BALANCE } from '../../config/balance';
import { DIFFICULTIES } from '../../config/difficulty';
import {
  FLIR,
  FONT,
  formatTime,
  makeButton,
  makePanel,
  makeScreenRoot,
  makeSegmented,
  makeTitle,
  el,
  type ButtonHandle,
  type SegmentedHandle,
} from './ui';

/** Typewriter reveal speed (characters per second). */
const TYPE_CPS = 55;

export interface BriefingScreenOptions {
  /** Live save (difficulty read/written here — it applies to this deploy). */
  save: SaveStateV1;
  /** Confirm: actually start the mission. */
  onDeploy: (missionId: string) => void;
  /** Back out to the menu. */
  onBack: () => void;
}

export class BriefingScreen implements Screen<string> {
  private readonly opts: BriefingScreenOptions;
  private root: HTMLDivElement | null = null;
  private deployBtn: ButtonHandle | null = null;
  private backBtn: ButtonHandle | null = null;
  private diffSeg: SegmentedHandle | null = null;
  private typeTimer = 0;

  constructor(opts: BriefingScreenOptions) {
    this.opts = opts;
  }

  mount(parent: HTMLElement, missionId: string): void {
    const mission = getMission(missionId);
    const { save } = this.opts;

    const root = makeScreenRoot();
    root.style.gap = '16px';

    // Fill the frame instead of a thin centre column (owner: "use the width").
    const WIDE = 'min(1040px, 94vw)';

    const kicker = el(
      'div',
      {
        font: `700 clamp(11px, 2vw, 13px)/1 ${FONT}`,
        letterSpacing: '0.16em',
        color: FLIR.greenDim,
      },
      'MISSION BRIEFING',
    );

    const title = makeTitle(mission.name.toUpperCase());
    title.style.font = `700 clamp(30px, 6vw, 46px)/1.04 ${FONT}`;
    title.style.letterSpacing = '0.05em';

    const objective =
      mission.objective === 'survive'
        ? `HOLD ${formatTime(mission.durationMs)}`
        : mission.objective === 'endless'
          ? 'ENDLESS — SURVIVE'
          : mission.objective.toUpperCase();
    const meta = el(
      'div',
      {
        font: `600 clamp(12px, 2.2vw, 15px)/1.4 ${FONT}`,
        letterSpacing: '0.05em',
        color: FLIR.accent,
        textAlign: 'center',
      },
      `${mission.biome.toUpperCase()}   ·   ${objective}`,
    );

    // ── WIDE two-column body: orders (left) · the deal (right). On a narrow
    //    screen the columns fall back to a single stack (auto-fit). ──────────
    const body = el('div', {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(min(420px, 100%), 1fr))',
      gap: '16px',
      width: WIDE,
      alignItems: 'start',
    });
    const leftCol = el('div', { display: 'flex', flexDirection: 'column', gap: '12px' });
    const rightCol = el('div', { display: 'flex', flexDirection: 'column', gap: '12px' });

    // ── The transmission card: speaker tag + typewriter brief ───────────────
    const panel = makePanel({ cursor: 'pointer' });
    const speaker = el(
      'div',
      {
        font: `700 12px/1 ${FONT}`,
        letterSpacing: '0.1em',
        color: FLIR.greenDim,
        marginBottom: '10px',
      },
      'OVERLORD ACTUAL — INBOUND TX',
    );
    const briefText = el('div', {
      font: `500 clamp(14px, 2.4vw, 17px)/1.7 ${FONT}`,
      letterSpacing: '0.01em',
      color: FLIR.green,
      minHeight: '4.5em', // reserve the typed height so the card never jumps
      whiteSpace: 'pre-wrap',
    });
    panel.append(speaker, briefText);

    // Typewriter reveal; tapping the card completes it instantly.
    const full = mission.brief;
    let shown = 0;
    const stepMs = 1000 / TYPE_CPS;
    this.typeTimer = window.setInterval(() => {
      shown += 1;
      briefText.textContent = full.slice(0, shown);
      if (shown >= full.length) this.stopTyping();
    }, stepMs);
    panel.addEventListener('click', () => {
      this.stopTyping();
      briefText.textContent = full;
    });

    // ── ROE chip when friendlies walk the grid (the IFF teaching beat) ──────
    const hasFriendlies = mission.waves.some((w) => (w.spawns.trooper ?? 0) > 0);
    const roe = hasFriendlies
      ? el(
          'div',
          {
            font: `700 clamp(12px, 2.2vw, 14px)/1.4 ${FONT}`,
            letterSpacing: '0.05em',
            color: FLIR.warn,
            border: `1px solid ${FLIR.warn}`,
            borderRadius: '8px',
            padding: '10px 14px',
            textAlign: 'center',
          },
          'ROE — 3 friendly-fire strikes = mission fail',
        )
      : null;
    leftCol.append(panel);
    if (roe) leftCol.append(roe);

    // ── THE DEAL: the economy in two lines, not three paragraphs. Kill pays
    //    SCRIP × combo, banked win or lose, spent in the Armory. ─────────────
    const maxCombo = BALANCE.combo.steps[BALANCE.combo.steps.length - 1] ?? 4;
    const intel = makePanel({ padding: '16px 20px' });
    intel.append(
      el(
        'div',
        { font: `700 12px/1 ${FONT}`, letterSpacing: '0.1em', color: FLIR.greenDim, marginBottom: '12px' },
        'THE PAY',
      ),
      el(
        'div',
        {
          font: `700 clamp(17px, 3vw, 22px)/1.25 ${FONT}`,
          letterSpacing: '0.02em',
          color: FLIR.accent,
        },
        `Every kill → SCRIP × combo (up to ×${maxCombo})`,
      ),
      el(
        'div',
        {
          font: `500 clamp(13px, 2.3vw, 15px)/1.55 ${FONT}`,
          letterSpacing: '0.01em',
          color: FLIR.green,
          marginTop: '8px',
        },
        'Banked win or lose. Spend it in the ARMORY on guns and upgrades.',
      ),
      el(
        'div',
        {
          font: `500 clamp(11px, 2vw, 13px)/1.5 ${FONT}`,
          letterSpacing: '0.01em',
          color: FLIR.greenDim,
          marginTop: '8px',
        },
        `Elites also drop SALVAGE — ${BALANCE.armory.scripPerSalvage} scrip each toward retrofits.`,
      ),
    );

    // ── Difficulty (applies to THIS deploy) ─────────────────────────────────
    const diffRow = makePanel({ padding: '10px 20px' });
    this.diffSeg = makeSegmented(
      'DIFFICULTY',
      Object.values(DIFFICULTIES).map((d) => ({ value: d.id, label: d.name.toUpperCase() })),
      save.difficulty,
      (v) => {
        save.difficulty = v as Difficulty;
        writeSave(save);
      },
    );
    diffRow.append(this.diffSeg.el);
    rightCol.append(intel, diffRow);

    body.append(leftCol, rightCol);

    // ── Actions ─────────────────────────────────────────────────────────────
    const actions = el('div', {
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'center',
      gap: '14px',
      marginTop: '4px',
    });
    this.backBtn = makeButton('BACK', () => this.opts.onBack(), { width: 'min(200px, 38vw)' });
    this.deployBtn = makeButton('DEPLOY', () => this.opts.onDeploy(mission.id), {
      accent: true,
      width: 'min(280px, 52vw)',
    });
    actions.append(this.backBtn.el, this.deployBtn.el);

    root.append(kicker, title, meta, body, actions);
    parent.appendChild(root);
    this.root = root;
  }

  private stopTyping(): void {
    if (this.typeTimer) {
      window.clearInterval(this.typeTimer);
      this.typeTimer = 0;
    }
  }

  unmount(): void {
    this.stopTyping();
    this.deployBtn?.destroy();
    this.backBtn?.destroy();
    this.diffSeg?.destroy();
    this.deployBtn = null;
    this.backBtn = null;
    this.diffSeg = null;
    this.root?.remove();
    this.root = null;
  }
}
